import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSetCardDeadline } from '@/entities/board';
import type { CardDetail } from '@/entities/card';
import { useViewer } from '@/entities/viewer';
import { FormError } from '@/shared/forms';
import {
  type DeadlineDraft,
  draftFromDeadline,
  rangeToPayload,
  validateRange,
} from '@/shared/lib/deadline-draft';
import { DateProperty } from '@/shared/ui';

import styles from './DeadlineEditor.module.css';

const SAVE_DELAY_MS = 450;

const signature = (draft: DeadlineDraft) =>
  [
    draft.startDate,
    draft.startTime,
    draft.endDate,
    draft.endTime,
    draft.startDate !== '' && draft.overdueEnabled === true,
  ].join('|');

/**
 * Card dates: Start and End are always visible. The draft is validated locally and only a VALID
 * one is sent (a start without an end stays on screen with its message and is never persisted),
 * shortly after the last change: no Save button. Clearing both fields removes the date.
 */
export function DeadlineEditor({ card }: { card: CardDetail }) {
  const { t } = useTranslation();
  const setDeadline = useSetCardDeadline();
  const viewer = useViewer();
  // Where new or changed dates are interpreted: the profile timezone.
  const zone = viewer.status === 'authenticated' ? viewer.user.timezone : null;
  const serverDraft = useMemo(() => draftFromDeadline(card.deadline), [card.deadline]);
  const [draft, setDraft] = useState<DeadlineDraft>(serverDraft);
  const [dirty, setDirty] = useState(false);
  const timer = useRef<number | null>(null);
  const [seen, setSeen] = useState(signature(serverDraft));

  // Follow the server (another tab, a calendar drag) unless the user is in the middle of an edit.
  const current = signature(serverDraft);
  if (current !== seen) {
    setSeen(current);
    if (!dirty) setDraft(serverDraft);
  }

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  const errors = useMemo(() => validateRange(draft), [draft]);

  const change = (next: DeadlineDraft) => {
    setDraft(next);
    setDirty(true);
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
    if (validateRange(next).length > 0) return; // never persist an invalid intermediate state
    if (signature(next) === signature(serverDraft)) {
      setDirty(false);
      return;
    }
    timer.current = window.setTimeout(() => {
      timer.current = null;
      setDeadline.mutate(
        { cardId: card.id, deadline: rangeToPayload(next) },
        {
          onSuccess: () => {
            setDirty(false);
          },
        },
      );
    }, SAVE_DELAY_MS);
  };

  return (
    <div className={styles.section}>
      <DateProperty value={draft} onChange={change} errors={errors} timezone={zone} />
      {card.is_overdue && !dirty && (
        <p className={styles.overdue}>
          <span aria-hidden>⚠ </span>
          {t('deadline.state.overdue')}
        </p>
      )}
      <FormError error={setDeadline.error} />
    </div>
  );
}
